import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Input, InputNumber, Tooltip, message } from 'antd';
import { ReferralRemove as ReferralRemoveIcon } from 'common/components/svgs';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import EnhancedInputNumber from '~client/components/enhanced-input-number';

const defaultTier = {
  shortTermCalculationValue: null,
  longTermCalculationValue: null,
  bookingCountFrom: 0,
  bookingCountTo: null,
  name: '',
};
/*
  errInfo:{
  haveNoError: boolean,
  validateErrorDetail: {
    errMsg:string,
    errIndex:number,
    errKey:string
    }[]
  }
 */
const TiersGroup = ({
  tiersValue,
  onChange,
  onRequestValidate,
  errInfo,
  unit,
  calculationType,
}) => {
  const { t } = useTranslation();
  const [tiers, setTiers] = useState(
    tiersValue.length ? tiersValue : [Object.assign({}, defaultTier)],
  );

  useEffect(() => {
    if (tiers !== tiersValue) {
      onChange(tiers);
    }
  }, [tiers]);

  const firstErrInfoItem = useMemo(() => {
    return errInfo?.validateErrorDetail.length ? errInfo.validateErrorDetail[0] : null;
  }, [errInfo]);

  const getErrMsg = useCallback(
    (errKey, errIndex) => {
      const result = errInfo?.validateErrorDetail.find(
        d => d.errIndex === errIndex && d.errKey === errKey,
      )?.errMsg;
      return result;
    },
    [errInfo],
  );

  const trySortTier = waitSortTiers => {
    const tiersEndPosistionIndex = waitSortTiers.length - 1;
    let prevBookingCountTo = -1;
    return waitSortTiers.map((tier, tierIndex) => {
      const isFirstTier = tierIndex === 0;
      const isLastTier = tierIndex === tiersEndPosistionIndex;

      let currentbookingCountFrom = tier.bookingCountFrom;
      let currentBookingCountTo = tier.bookingCountTo;

      //if is firstTier ,make sure countFrom is Zero
      if (isFirstTier) {
        currentbookingCountFrom = 0;
      }
      //if not first tier and countFrom is not same with prevCountTo+1,then fix it
      if (!isFirstTier && currentbookingCountFrom !== prevBookingCountTo + 1) {
        currentbookingCountFrom = prevBookingCountTo + 1;
      }
      //if only have one tier,make sure countFrom===0
      if (isFirstTier && isLastTier) {
        currentbookingCountFrom = 0;
      }

      //if countFrom === countTo, increase 1.
      if (currentbookingCountFrom === currentBookingCountTo) {
        currentBookingCountTo = +1;
      }
      if (!isLastTier && currentBookingCountTo < currentbookingCountFrom) {
        currentBookingCountTo = currentbookingCountFrom + 1;
      }
      //if only have one tier,make sure countTo===0
      if (isLastTier) {
        currentBookingCountTo = null;
      }
      prevBookingCountTo = currentBookingCountTo;
      return {
        ...tier,
        bookingCountFrom: currentbookingCountFrom,
        bookingCountTo: currentBookingCountTo,
      };
    });
  };
  const handleAddTier = () => {
    if (tiers.length === 10) {
      message.warn(t('cms.referral.commission_plan.modal.tier_max_limit'));
      return;
    }
    if (tiers.length > 0) {
      const endTier = Object.assign({}, tiers[tiers.length - 1]);
      endTier.bookingCountTo = endTier.bookingCountFrom + 1;
      const prevBookingCountTo = endTier.bookingCountTo;
      const newTiersList = tiers.slice(0, tiers.length - 1).concat([
        endTier,
        {
          ...defaultTier,
          bookingCountFrom: prevBookingCountTo + 1,
          bookingCountTo: null,
        },
      ]);
      setTiers(newTiersList);
    }
    if (tiers.length === 0) {
      setTiers(defaultTier);
    }
  };

  const handleDeleteTier = waitDeleteTierIndex => {
    if (tiers.length <= 1) {
      message.warn(t('cms.referral.commission_plan.modal.tier_min_limit'));
      return;
    }
    const newTiersList = tiers.filter((_tier, tierIndex) => tierIndex !== waitDeleteTierIndex);
    setTiers(trySortTier(newTiersList));
  };

  const handleUpdateTier = (tierField, tierFieldValue, tierIndex) => {
    const waitUpdateTier = [...tiers];
    waitUpdateTier[tierIndex][tierField] = tierFieldValue;
    setTiers(trySortTier(waitUpdateTier));
  };

  return (
    <>
      <div>
        <div className="commission-plan-editor__tiers-group__tr commission-plan-editor__tiers-group--mb-8">
          <div className="commission-plan-editor__tiers-group__th commission-plan-editor__tiers-group--flex commission-plan-editor__tiers-group--mr-16">
            <div className="commission-plan-editor__tiers-group__th commission-plan-editor__tiers-group__th--120 commission-plan-editor__tiers-group--flex ant-legacy-form-item-required">
              {t('cms.referral.commission_plan.modal.from_number.label')}
              <Tooltip
                title={t('cms.referral.commission_plan.modal.from_number.tip')}
                placement="top"
                className="commission-plan-editor__tiers-group--ml-4"
              >
                <QuestionCircleOutlined className="commission-plan-editor__tiers-group--ml-4 commission-plan-editor__tiers-group__tooltip-icon" />
              </Tooltip>
            </div>
            <div className="commission-plan-editor__tiers-group--span-39"></div>
            <div className="commission-plan-editor__tiers-group__th commission-plan-editor__tiers-group__th--120 commission-plan-editor__tiers-group--flex ant-legacy-form-item-required">
              {t('cms.referral.commission_plan.modal.to_number.label')}
              <Tooltip
                title={t('cms.referral.commission_plan.modal.to_number.tip')}
                placement="top"
              >
                <QuestionCircleOutlined className="commission-plan-editor__tiers-group--ml-4 commission-plan-editor__tiers-group__tooltip-icon" />
              </Tooltip>
            </div>
          </div>
          <div className="commission-plan-editor__tiers-group__th commission-plan-editor__tiers-group__th--150 commission-plan-editor__tiers-group--mr-16 commission-plan-editor__tiers-group--flex ant-legacy-form-item-required">
            {`${t('cms.referral.commission_plan.modal.short_term.label')}`}
          </div>
          <div className="commission-plan-editor__tiers-group__th commission-plan-editor__tiers-group__th--150 commission-plan-editor__tiers-group--mr-16 commission-plan-editor__tiers-group--flex ant-legacy-form-item-required">
            {`${t('cms.referral.commission_plan.modal.long_term.label')}`}
          </div>
          <div className="commission-plan-editor__tiers-group__th  commission-plan-editor__tiers-group__th--150 commission-plan-editor__tiers-group--mr-16 ant-legacy-form-item-required">
            {t('cms.referral.commission_plan.modal.tier_name.label')}
          </div>
          <div className="commission-plan-editor__tiers-group__th"></div>
        </div>
        <For of={tiers} each="tier" index="tierIndex">
          <div
            className={classNames('commission-plan-editor__tiers-group__tr', {
              'commission-plan-editor__tiers-group--mb-12': tierIndex !== tiers.length - 1,
            })}
            key={tierIndex}
          >
            <div className="commission-plan-editor__tiers-group--flex commission-plan-editor__tiers-group--mr-16">
              <div className="commission-plan-editor__tiers-group__td commission-plan-editor__tiers-group__td--120 commission-plan-editor__tiers-group--mr-16">
                <InputNumber
                  className="commission-plan-editor__tiers-group__input"
                  formatter={v => {
                    return tierIndex === tiers.length - 1 ? `≥ ${v}` : v;
                  }}
                  disabled
                  value={tier.bookingCountFrom}
                />
              </div>
              <span className="commission-plan-editor__tiers-group--mr-16 commission-plan-editor__tiers-group__th commission-plan-editor__tiers-group--w-7">
                —
              </span>
              <div
                className={classNames(
                  'commission-plan-editor__tiers-group__td commission-plan-editor__tiers-group__td--120',
                  {
                    'has-error': getErrMsg('bookingCountTo', tierIndex),
                  },
                )}
              >
                <InputNumber
                  value={tier.bookingCountTo}
                  formatter={v => {
                    return tierIndex === tiers.length - 1 && v.length === 0 ? '—' : v;
                  }}
                  disabled={tierIndex === tiers.length - 1}
                  onChange={v => handleUpdateTier('bookingCountTo', v, tierIndex)}
                  onBlur={() => {
                    onRequestValidate(tierIndex, ['bookingCountTo']);
                  }}
                />
              </div>
            </div>
            <div
              className={classNames(
                'commission-plan-editor__tiers-group__td commission-plan-editor__tiers-group__td--150 commission-plan-editor__tiers-group--mr-16',
                {
                  'has-error': getErrMsg('shortTermCalculationValue', tierIndex),
                },
              )}
            >
              <EnhancedInputNumber
                min={0}
                max={calculationType === 'PERCENTAGE' ? 100 : undefined}
                value={tier.shortTermCalculationValue}
                onChange={v => handleUpdateTier('shortTermCalculationValue', v, tierIndex)}
                onBlur={() => {
                  onRequestValidate(tierIndex, ['shortTermCalculationValue']);
                }}
                addonAfter={unit}
              />
            </div>
            <div
              className={classNames(
                'commission-plan-editor__tiers-group__td commission-plan-editor__tiers-group__td--150 commission-plan-editor__tiers-group--mr-16',
                {
                  'has-error': getErrMsg('longTermCalculationValue', tierIndex),
                },
              )}
            >
              <EnhancedInputNumber
                min={0}
                max={calculationType === 'PERCENTAGE' ? 100 : undefined}
                value={tier.longTermCalculationValue}
                onChange={v => handleUpdateTier('longTermCalculationValue', v, tierIndex)}
                onBlur={() => {
                  onRequestValidate(tierIndex, ['longTermCalculationValue']);
                }}
                addonAfter={unit}
              />
            </div>
            <div
              className={classNames(
                'commission-plan-editor__tiers-group__td commission-plan-editor__tiers-group__td--150 commission-plan-editor__tiers-group--mr-16',
                {
                  'has-error': getErrMsg('name', tierIndex),
                },
              )}
            >
              <Input
                autoComplete="off"
                value={tier.name}
                onChange={e => handleUpdateTier('name', e.currentTarget.value, tierIndex)}
                onBlur={() => {
                  onRequestValidate(tierIndex, ['name']);
                }}
              />
            </div>
            <div
              className="commission-plan-editor__tiers-group__td commission-plan-editor__tiers-group--flex commission-plan-editor__tiers-group--cursor-pointer"
              onClick={() => handleDeleteTier(tierIndex)}
            >
              <ReferralRemoveIcon />
            </div>
          </div>
        </For>

        {firstErrInfoItem?.errMsg ? (
          <div className="has-error">
            <div className="ant-legacy-form-explain">{firstErrInfoItem?.errMsg}</div>
          </div>
        ) : null}

        <span
          className="commission-plan-editor__tiers-group--mt-16 commission-plan-editor__tiers-group--mb-32 commission-plan-editor__tiers-group__link commission-plan-editor__tiers-group--cursor-pointer"
          onClick={() => handleAddTier()}
        >
          {t('cms.referral.commission_plan.modal.add_new_tier.label')}
        </span>
      </div>
    </>
  );
};

export default TiersGroup;
